import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField,
  Grid, Divider, Stack, Snackbar, Alert, Paper, IconButton,
  InputAdornment, Chip, CircularProgress
} from '@mui/material';
import {
  Person as PersonIcon, Lock as LockIcon, Save as SaveIcon,
  Email as EmailIcon, Badge as BadgeIcon, CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon, PhotoCamera as PhotoCameraIcon,
  Visibility, VisibilityOff, Phone as PhoneIcon, Cancel as CancelIcon
} from '@mui/icons-material';
import { authAPI, userAPI } from '../../services/api';
import UserAvatar from '../common/UserAvatar';
import { useAuth } from '../../hooks/useAuth';

const AccountSettings = () => {
  const { user: authUser, updateUser } = useAuth();
  const [user, setUser] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [profileData, setProfileData] = useState({ name: '', email: '', phone_number: '' });
  const [originalProfile, setOriginalProfile] = useState({ name: '', email: '', phone_number: '' });

  const [passwordData, setPasswordData] = useState({
    current_password: '', password: '', password_confirmation: ''
  });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => { fetchUserData(); }, []);

  const fetchUserData = async () => {
    try {
      const response = await authAPI.getUser();
      const data = response.data;
      const { permissions: _, teacher_id: __, ...userData } = data;
      setUser(userData);
      updateUser(userData);
      setProfileData({
        name: userData.name || '',
        email: userData.email || '',
        phone_number: userData.phone_number || ''
      });
      setOriginalProfile({
        name: userData.name || '',
        email: userData.email || '',
        phone_number: userData.phone_number || ''
      });
    } catch {
      setSnackbar({ open: true, message: 'Failed to load user data', severity: 'error' });
    } finally {
      setFetchLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      const response = await authAPI.updateProfile({ name: profileData.name, email: profileData.email });
      const emailChanged = profileData.email !== originalProfile.email;
      const serverUser = response.data.user;
      const updatedUser = {
        ...user,
        ...serverUser,
        email_verified_at: emailChanged ? null : serverUser.email_verified_at
      };
      setUser(updatedUser);
      updateUser(updatedUser);
      setOriginalProfile({ ...originalProfile, name: profileData.name, email: profileData.email });
      setSnackbar({ open: true, message: emailChanged ? 'Profile updated. You will need to verify your new email on next login.' : 'Profile updated successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to update profile', severity: 'error' });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.password !== passwordData.password_confirmation) {
      setSnackbar({ open: true, message: 'Passwords do not match', severity: 'error' });
      return;
    }
    setPasswordLoading(true);
    try {
      await authAPI.changePassword(passwordData);
      setSnackbar({ open: true, message: 'Password changed successfully', severity: 'success' });
      setPasswordData({ current_password: '', password: '', password_confirmation: '' });
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to change password', severity: 'error' });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !user?.id) return;
    setProfileLoading(true);
    try {
      const formData = new FormData();
      formData.append('profile_picture', file);
      await userAPI.uploadProfilePicture(user.id, formData);
      setSnackbar({ open: true, message: 'Profile picture updated', severity: 'success' });
      fetchUserData();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to upload picture', severity: 'error' });
    } finally {
      setProfileLoading(false);
    }
  };

  const profileChanged = profileData.name !== originalProfile.name || profileData.email !== originalProfile.email;
  const passwordFilled = passwordData.current_password.length > 0 && passwordData.password.length > 0 && passwordData.password_confirmation.length > 0;

  const VerificationBadge = ({ verified }) => (
    <Chip
      size="small"
      icon={verified ? <CheckCircleIcon /> : <CancelIcon />}
      label={verified ? 'Verified' : 'Not Verified'}
      color={verified ? 'success' : 'warning'}
      variant="outlined"
      sx={{ ml: 1 }}
    />
  );

  if (fetchLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
          Account Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your account information and security
        </Typography>
      </Box>

      <Grid container spacing={{ xs: 2, md: 3 }}>

        {/* Profile Information */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                <PersonIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Profile Information</Typography>
              </Stack>

              <Box display="flex" justifyContent="center" mb={3} position="relative">
                <UserAvatar user={user} size={100} />
                <IconButton
                  component="label"
                  sx={{
                    position: 'absolute', bottom: 0, right: '50%',
                    transform: 'translateX(50%)', bgcolor: 'primary.main',
                    color: 'white', '&:hover': { bgcolor: 'primary.dark' },
                    width: 32, height: 32
                  }}
                >
                  <PhotoCameraIcon sx={{ fontSize: 18 }} />
                  <input type="file" hidden accept="image/*" onChange={handleProfilePictureChange} />
                </IconButton>
              </Box>

              <Box component="form" onSubmit={handleUpdateProfile}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth size="small" label="Full Name" required
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    InputProps={{ startAdornment: <BadgeIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                  />

                  <Box>
                    <TextField
                      fullWidth size="small" label="Email Address" type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      InputProps={{ startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                      helperText={user?.email && !user?.email_verified_at ? 'Email not verified — changing it will require re-verification' : ''}
                    />
                    {user?.email && (
                      <Box mt={0.5}>
                        <VerificationBadge verified={!!user.email_verified_at} />
                      </Box>
                    )}
                  </Box>

                  <Box>
                    <TextField
                      fullWidth size="small" label="Phone Number"
                      value={profileData.phone_number}
                      disabled
                      InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                      helperText="Phone number can only be changed by an administrator"
                    />
                    {user?.phone_number && (
                      <Box mt={0.5}>
                        <VerificationBadge verified={!!user.phone_verified_at} />
                      </Box>
                    )}
                  </Box>

                  <Button
                    type="submit" variant="contained" color="primary"
                    startIcon={profileLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    disabled={profileLoading || !profileChanged} fullWidth
                  >
                    {profileLoading ? 'Saving...' : 'Update Profile'}
                  </Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Change Password */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                <LockIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Change Password</Typography>
              </Stack>

              <Box component="form" onSubmit={handleChangePassword}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth size="small" label="Current Password" required
                    type={showCurrent ? 'text' : 'password'}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowCurrent(!showCurrent)}>
                            {showCurrent ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  <Divider />

                  <TextField
                    fullWidth size="small" label="New Password" required
                    type={showNew ? 'text' : 'password'}
                    value={passwordData.password}
                    onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                    helperText="Minimum 8 characters"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowNew(!showNew)}>
                            {showNew ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  <TextField
                    fullWidth size="small" label="Confirm New Password" required
                    type={showConfirm ? 'text' : 'password'}
                    value={passwordData.password_confirmation}
                    onChange={(e) => setPasswordData({ ...passwordData, password_confirmation: e.target.value })}
                    error={passwordData.password_confirmation.length > 0 && passwordData.password !== passwordData.password_confirmation}
                    helperText={passwordData.password_confirmation.length > 0 && passwordData.password !== passwordData.password_confirmation ? 'Passwords do not match' : ''}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowConfirm(!showConfirm)}>
                            {showConfirm ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  <Button
                    type="submit" variant="contained" color="primary"
                    startIcon={passwordLoading ? <CircularProgress size={16} color="inherit" /> : <LockIcon />}
                    disabled={passwordLoading || !passwordFilled} fullWidth
                  >
                    {passwordLoading ? 'Changing...' : 'Change Password'}
                  </Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Information */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <CalendarIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Account Information</Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {[
                  { label: 'Account Created', value: user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A' },
                  { label: 'Last Updated', value: user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A' },
                ].map(({ label, value }) => (
                  <Grid item xs={6} sm={6} md={3} key={label}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {label}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="primary.main">
                        {value}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
                <Grid item xs={6} sm={6} md={3}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Account Status
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {user?.is_active
                        ? <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        : <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                      <Typography variant="body2" fontWeight={600} color={user?.is_active ? 'success.main' : 'error.main'}>
                        {user?.is_active ? 'Active' : 'Inactive'}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountSettings;
